import { useState } from 'react';
import { toast } from 'sonner';
import type { ActiveFlag, ActiveNote, ActiveTask, Station } from '@hcc/types';
import { useAppendEvent } from '../../hooks/usePeople';
import { useIntentKey } from '../../hooks/useIntentKey';
import { FlagBanner } from '../person/FlagBanner';
import { NotesPanel } from '../person/NotesPanel';
import { AddNoteModal } from '../person/AddNoteModal';

interface PersonContextPanelProps {
  personId: string;
  personName: string;
  station: Station;
  flags: ActiveFlag[];
  notes: ActiveNote[];
  tasks: ActiveTask[];
}

/**
 * The cross-desk context block at the top of the kit-issue drawer: active
 * flags, station-tagged task reminders, and the notes panel with add/remove.
 * Owns its own event appends so the drawer only handles kit.
 */
export function PersonContextPanel({
  personId,
  personName,
  station,
  flags,
  notes,
  tasks,
}: PersonContextPanelProps) {
  const append = useAppendEvent(personId);
  // Separate idempotency key for the at-desk note (a distinct write intent).
  const noteIntent = useIntentKey();
  const [addingNote, setAddingNote] = useState(false);

  // Tasks tagged for this station appear as a reminder strip so staff can
  // prompt the inductee verbally ("don't forget your DBS form"). Read-only
  // here; HR / coord manage them from the admin panel.
  const stationVisibilityTag = `station_${station.toLowerCase()}`;
  const reminders = tasks.filter(
    (t) =>
      (t.visibility as readonly string[]).includes(stationVisibilityTag) && t.status !== 'complete',
  );

  async function addNote(input: { text: string; station?: Station }) {
    await append.mutateAsync({
      event_type: 'NoteAdded',
      payload: input,
      client_event_id: noteIntent.key,
    } as Parameters<typeof append.mutateAsync>[0]);
    noteIntent.renew();
    toast.success('Note added');
  }

  async function removeNote(note: ActiveNote) {
    await append.mutateAsync({
      event_type: 'NoteRemoved',
      payload: { note_event_id: note.event_id },
    } as Parameters<typeof append.mutateAsync>[0]);
    toast.success('Note removed');
  }

  return (
    <>
      {flags.length > 0 ? (
        <div className="mb-4">
          <FlagBanner flags={flags} />
        </div>
      ) : null}

      {reminders.length > 0 ? (
        <div className="mb-4 rounded-token-sm border border-warning/30 bg-warning-soft/15 px-3 py-2 text-xs text-warning-soft-fg">
          <div className="font-semibold uppercase tracking-wider text-[10px] text-warning-soft-fg/80">
            Reminders for this station
          </div>
          <ul className="mt-1 space-y-0.5">
            {reminders.map((t) => (
              <li key={t.task_id}>
                <span className="font-medium">{t.title}</span>
                {t.due_at ? <span className="ml-1 opacity-80">· due {t.due_at}</span> : null}
                {t.status === 'changes_requested' ? (
                  <span className="ml-1 text-danger-soft-fg">· changes needed</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mb-4">
        <NotesPanel notes={notes} onAdd={() => setAddingNote(true)} onRemove={removeNote} collapsible />
      </div>
      <AddNoteModal
        open={addingNote}
        personName={personName}
        station={station}
        onClose={() => setAddingNote(false)}
        onSubmit={addNote}
      />
    </>
  );
}
